import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject, Ref } from 'react';
import { useRef } from 'react';
import { useScrollTransition } from '../../../util/react-transition-component';
import type { OfficeOverviewProps, OfficeOverviewRef } from './LocationOverview.data';
import styles from './LocationOverview.module.scss';
import { setupTransitionInTimeline } from './LocationOverview.transitions';

import DynamicComponent from '../../DynamicComponent';
import OfficeDetails from '../office-details/OfficeDetails';
import Heading from '../../atoms/heading/Heading';
import { useRouter } from 'next/router';
import Script from 'next/script';

export default function LocationOverview({
  className,
  blok,
  canTransitionIn,
  ref,
}: OfficeOverviewProps & { ref?: Ref<OfficeOverviewRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  // const titleRef = useRef<HTMLHeadingElement>(null);
  const imageHolderRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${process.env.SITE_URL}`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Locations',
        item: `${process.env.SITE_URL}/locations`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: blok.title,
        item: `${process.env.SITE_URL}/${router.asPath}`,
      },
    ],
  };

  useScrollTransition({
    scroll: {
      canTransitionIn,
      trigger: elementRef,
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        imageHolderRef,
      },
      setupTransitionInTimeline,
    }),
  });

  return (
    <div
      ref={elementRef}
      className={classNames(styles.officeOverview, className)}
      {...sbEditable(blok)}
    >
      <div
        // ref={titleWrapperRef}
        className={classNames(styles.titleWrapper)}
      >
        <Heading
          // ref={titleRef}
          transitionInType="splitWordsSlideInUp"
          className={classNames(styles.title)}
          as="h1"
          size="heading-01"
          canTransitionIn={canTransitionIn}
          disableTriggerInOnScroll={false}
          // isVisible={{
          //   onMount: true,
          //   value: true,
          // }}
        >
          {blok.title}
        </Heading>
      </div>

      {blok?.office_details?.[0] && (
        <OfficeDetails blok={blok.office_details[0]} className={classNames(styles.officeDetails)} />
      )}

      {blok.sections?.map((module) => (
        <DynamicComponent key={module._uid} blok={module} canTransitionIn={canTransitionIn} />
      ))}

      <Script
        id="breadcrumbs-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />
    </div>
  );
}
